import { Mutex } from 'async-mutex'
import { getCurrentAuthState } from '~/atoms/AuthenticationState'
import { getApiInst } from '~/atoms/MangoApi'
import { domains } from '~/constants/domains'
import emitterService from '~/services/EventEmitter/EmitterService'
import {
  AuthenticationEmitterEventType,
  AuthenticationUpdatePayload,
} from '~/services/EventEmitter/types'
import Logger from '~/services/Logger'
import { AnalyticsNamespace } from '~/types/Analytics/AnalyticsNamespaces'
import { WebSocketAnalyticsEvents } from '~/types/Analytics/WebSocketAnalyticsEvents'
import {
  AuthenticationState,
  AuthenticationUpdateType,
} from '~/types/AuthenticationTypes'
import {
  WebSocketCommand,
  WebSocketCommandType,
  WebSocketEvent,
  WebSocketEventType,
} from '~/types/WebSocketTypes'
import { Analytics } from '~/utils/analytics'
import { isAccessTokenExpired } from '~/utils/functions'

const NORMAL_CLOSURE = 1000
const STARTING_RETRY_IN_MS = 250
const MAX_RECONNECT_RETRIES = 10

interface Unsubscribe {
  (): void
}

type WebSocketEventCallback = (
  event: WebSocketEvent<WebSocketEventType>,
) => void

interface WebSocketServiceControl {
  /** Subscribes the given callback as a recipient of WebSocketEvents
   * @param callback The callback to be invoked when a WebSocketEvent is received
   * @returns An unsubscribe function that will remove the callback from the list of recipients
   */
  subscribe: (callback: WebSocketEventCallback) => Unsubscribe
  /** Sends the given message to the WebSocket server */
  sendMessage: (message: WebSocketCommand<WebSocketCommandType>) => void
  /** Closes the WebSocket connection */
  close: () => void
}

export default class WebSocketService implements WebSocketServiceControl {
  private static _instance: WebSocketService | null = null
  private _pbxId: number | null = null
  private _websocket: WebSocket | null = null
  private _messagesToBeSent: WebSocketCommand<WebSocketCommandType>[] = []
  private _listeners: ((event: WebSocketEvent<WebSocketEventType>) => void)[] =
    []
  private _retryTimeOut = STARTING_RETRY_IN_MS
  private _numReconnectAttempts = 0
  private _logger = new Logger('WebSocketService')
  private _currentAuthState: AuthenticationState | null = null
  private getCurrentAccessToken = () =>
    this._currentAuthState?.appSessionJwts?.access_token
  private _reconnecting = false
  private _creationMutex = new Mutex()
  private _analytics = new Analytics(AnalyticsNamespace.WEB_SOCKET)

  private constructor() {
    window.addEventListener('offline', this.onOffline)
    window.addEventListener('online', this.onOnline)
    window.addEventListener('focus', this.onWindowFocused)
    emitterService.subscribe({
      eventType: AuthenticationEmitterEventType.USER_AUTH_DATA_UPDATED,
      callback: this.onAuthStateUpdated,
    })
    emitterService.subscribe({
      eventType: AuthenticationEmitterEventType.USER_LOGGED_OUT,
      callback: () => this.tryCloseSocket(),
    })
  }

  // #region Static Accessor
  public static getInstance(pbxId: number): WebSocketServiceControl {
    if (!WebSocketService._instance) {
      WebSocketService._instance = new WebSocketService()
    }

    const wssInst = WebSocketService._instance
    const hasPbxIdChanged = wssInst._pbxId !== pbxId
    const isWebSocketClosedAndNotReconnecting =
      !wssInst._websocket && !wssInst._reconnecting
    const shouldCreateNewSocket =
      !wssInst._websocket ||
      hasPbxIdChanged ||
      isWebSocketClosedAndNotReconnecting

    if (shouldCreateNewSocket) {
      //set the pbxid
      wssInst._pbxId = pbxId
      wssInst.tryCreate()
    }

    return WebSocketService._instance
  }
  // #endregion

  // #region Public Methods
  public sendMessage = (message: WebSocketCommand<WebSocketCommandType>) => {
    if (!this._websocket) {
      this._messagesToBeSent.push(message)
      return
    }

    let messageJson: string
    switch (this._websocket.readyState) {
      case WebSocket.CONNECTING:
        this._messagesToBeSent.push(message)
        break
      case WebSocket.OPEN:
        messageJson = JSON.stringify(message as Record<string, unknown>)
        this._websocket.send(messageJson)
        break
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        this._logger.info(
          'Message sent while socket is closed or closing - message queued until socket is open',
        )
        this._messagesToBeSent.push(message)

        //if reconnecting, don't try to recreate the socket
        if (!this._reconnecting) {
          this.tryCreate()
        }
        break
      default:
        break
    }
  }

  public subscribe = (callback: WebSocketEventCallback): Unsubscribe => {
    this._listeners.push(callback)

    return () => {
      const index = this._listeners.indexOf(callback)
      if (index === -1) {
        return
      }
      this._listeners.splice(index, 1)
    }
  }

  public close = () => {
    /**
     * Close with a "Normal Closure" code
     * https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
     */
    try {
      this.tryCloseSocket(NORMAL_CLOSURE)
    } catch (e: unknown) {
      this._logger.info('Web Socket failed to close', {
        closeError: e as Error,
        message: 'WEBSOCKET_CLOSE_FAILED',
        retryTimeout: this._retryTimeOut,
        error: e,
      })
    }
  }
  // #endregion

  private onOffline = () => {
    this._logger.info('Websocket offline')
    this.tryCloseSocket()
  }

  private onOnline = () => {
    //if we're already trying to reconnect, don't try to reconnect again
    if (this._reconnecting) {
      return
    }

    this._logger.info('Websocket online')
    this.tryCreate()
  }

  private onWindowFocused = () => {
    //if the websocket is in a disconnected state, try to reconnect
    if (
      !this._reconnecting &&
      (!this._websocket ||
        this._websocket.readyState === WebSocket.CLOSED ||
        this._websocket.readyState === WebSocket.CLOSING)
    ) {
      //already being handled by the tryCreate mechanism
      if (this._creationMutex.isLocked()) {
        return
      }

      this._logger.info('WSS disconnected on window focus - reconnecting...')
      this.tryCreate()
    }
  }

  private tryCreate = async () => {
    //if no pbx has been set in our scope, the mutex is locked, don't try to create a new socket, just bail gracefully
    if (this._pbxId === 0 || this._creationMutex.isLocked()) {
      return
    }

    const release = await this._creationMutex.acquire()
    try {
      this._logger.info('tryCreate mutex acquired - creating websocket...')

      if (!('WebSocket' in window || 'MozWebSocket' in window)) {
        this._logger.info('WebSocket object not found in window, bailing...')
        return
      }

      //if tryCreate is called and the websocket is already open or connecting, close it before proceeding
      if (
        this._websocket &&
        (this._websocket.readyState === WebSocket.OPEN ||
          this._websocket.readyState === WebSocket.CONNECTING)
      ) {
        this._logger.info('Websocket already exists, closing...')
        this.tryCloseSocket()
      }

      //kill the current auth state changed registration, get the current copy of the user's auth data
      this._currentAuthState = getCurrentAuthState()

      const currentAccessToken = this.getCurrentAccessToken()
      if (!currentAccessToken) {
        this._logger.info(
          'No access token - WSS cannot initiate websocket connection.',
        )
        return
      }

      const domain = domains.CHAT_RTM()
      if (!domain) {
        this._logger.info(
          'No chat rtm domain found, unable to connect over WSS...',
        )
        return
      }

      const token = this.getCurrentAccessToken()
      //pbxScope is a comma-delimited string of pbxIds - for now we are only going to scope to one pbx at a time - in the future if we enable notifications on the application across all PBXs a user has access to, we will need to update this query string.
      this._websocket = new WebSocket(
        `${domain}?token=${token}&pbxScope=${this._pbxId}`,
      )
      this._websocket.onopen = this.onWebSocketOpen
      this._websocket.onmessage = this.onWebSocketMessageReceived
      this._websocket.onclose = this.onWebSocketClose
      this._websocket.onerror = this.onWebSocketError

      this._logger.info('tryCreate completed, waiting on Open...')
    } finally {
      this._logger.info('tryCreate mutex released...')
      release()
    }
  }

  private onAuthStateUpdated = ({
    newAuthState,
    updateType,
  }: AuthenticationUpdatePayload) => {
    this._logger.info('Auth State Changed')

    //if the websocket already exists, check to see if the access token has changed, if it has, set the new authState object, and proceed
    if (this._websocket) {
      if (
        newAuthState.appSessionJwts?.access_token ===
        this.getCurrentAccessToken()
      ) {
        return
      }

      this._currentAuthState = newAuthState
      if (updateType !== AuthenticationUpdateType.AppSessionJwt) {
        return
      }
    }

    const accessToken = newAuthState.appSessionJwts?.access_token
    if (!accessToken || isAccessTokenExpired(accessToken)) {
      this._logger.info('Access token is expired or invalid')
      return
    }

    //if the socket is already open...no need to create a new one right now
    if (this._websocket?.readyState !== WebSocket.OPEN) {
      this._logger.info('Websocket exists, reconnect pending')
      this.tryCreate()
    }
  }

  private getPong = () => {
    return {
      eventType: WebSocketCommandType.Pong,
      eventTimestamp: new Date().toISOString(),
    } as WebSocketCommand<WebSocketCommandType.Pong>
  }

  private onWebSocketMessageReceived = ({ data }: MessageEvent) => {
    const message = JSON.parse(data) as WebSocketEvent<WebSocketEventType>
    if (message.eventType === WebSocketEventType.Ping) {
      this.sendMessage(this.getPong())
    } else {
      this._listeners.forEach((listener) => listener(message))
      this._logger.debug('Web Socket Message Received', {
        message,
        listeners: this._listeners.length,
      })
    }
  }

  private onWebSocketOpen = () => {
    this._logger.info('Websocket opened')
    this._messagesToBeSent.forEach((message) => {
      this.sendMessage(message)
    })

    this._messagesToBeSent = []
    this._retryTimeOut = STARTING_RETRY_IN_MS
    this._reconnecting = false
    this._numReconnectAttempts = 0
  }

  private tryRefreshCredentials = async () => {
    const currentAccessToken = this.getCurrentAccessToken()
    const expired =
      !currentAccessToken || isAccessTokenExpired(currentAccessToken)
    if (!expired) {
      return true
    }

    this._logger.info('Websocket closed due to expired token')

    //the token is expired or invalid - attempt to refresh - if refresh fails, stop entirely - the authentication system should take care of the rest.
    const apiInst = getApiInst()
    return await apiInst.verifyConnection()
  }

  private onWebSocketClose = async (event: CloseEvent) => {
    this._logger.info(`Websocket closed with code: ${event.code}`)

    if (event.code !== NORMAL_CLOSURE) {
      this._analytics.track(WebSocketAnalyticsEvents.UnexpectedClosure, {
        code: event.code,
        reason: event.reason,
      })
    }

    if (event.code === 1006) {
      console.trace({ event })
      // https://stackoverflow.com/questions/19304157/getting-the-reason-why-websockets-closed-with-close-code-1006
      this._logger.warn(
        'Abnormal closure detected (1006) - likely we did something fundementally wrong',
      )
    }

    if (!event.code || event.code === NORMAL_CLOSURE) {
      this._analytics.track(WebSocketAnalyticsEvents.NormalClosure)
      this._websocket = null
      return
    }

    //something else caused the issue, try to reconnect through the normal reconnect mechanism with exponential backoff
    //the next attempt to connect will be made when the app tries to send a message
    if (this._numReconnectAttempts > MAX_RECONNECT_RETRIES) {
      this._reconnecting = false
      this._logger.info(
        'Websocket failed to reconnect and has exhausted all retries',
        {
          message: 'WEBSOCKET_RECONNECT_FAILED_RETRIES_EXHAUSTED',
        },
      )
      return
    }

    await this.handleTryReconnect()
  }

  private handleTryReconnect = async () => {
    //wait for the timeout time
    await new Promise((resolve) => {
      setTimeout(resolve, this._retryTimeOut)
    })

    //after the wait, check to see if we've already reconnected
    if (this._websocket?.readyState === WebSocket.OPEN) {
      this._logger.info(
        'Websocket reconnect sequence stopped - socket is open.',
      )
      return
    }

    this._logger.info(
      `Websocket reconnect attempt ${this._numReconnectAttempts} of ${MAX_RECONNECT_RETRIES}`,
    )
    this._numReconnectAttempts++
    this._retryTimeOut += this._retryTimeOut
    this._reconnecting = true

    const refreshed = await this.tryRefreshCredentials()

    if (!refreshed) {
      //kill callbacks to end the loop
      this._reconnecting = false

      //close and dispose callbacks this time
      this.tryCloseSocket(undefined, true)

      //nothing left to do, the token is expired, and we can't refresh it - wait for the client application to force a logout and update the auth state
      this._logger.info(
        'Websocket closed due to expired token and refresh failed',
      )
      return
    }

    //always close before attempting reconnection
    this.tryCloseSocket()
    this.tryCreate()
  }

  private onWebSocketError = (event: Event) => {
    this._logger.info(`Websocket errored with event type: ${event.type}`, {
      event,
    })
    this.tryCloseSocket()
  }

  private tryCloseSocket = (code?: number, disposeCallbacks = false) => {
    if (this._websocket === null) {
      return
    }
    this._logger.info('Websocket closing & cleaning up...')

    //remove all listeners and remove the reference to the current websocket instance
    if (disposeCallbacks) {
      try {
        this._websocket.onopen = null
        this._websocket.onmessage = null
        this._websocket.onclose = null
        this._websocket.onerror = null
      } catch {
        //do nothing - we don't care if this throws an error but I don't want it to squawk
        //we just need to make sure that the listeners are removed
      }
    }

    //close can be safely called when the websocket is in any connection state
    this._websocket.close(code)
    this._websocket = null
  }
}
